import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useConsentCheck } from './../hooks/useConsentCheck';
import { IStore } from './../interfaces/StoreTypes';
import { fetchUserRevision } from './../store/reducers/userRevisionSlice';
import { buildLanguageRoute } from './../utils/languageRouteBuilder';
import {
    getGuestOpenScreensFromMenu,
    guestPrimaryRouteSegment,
    isGuestAllowedPrimarySegment,
    isGuestKidsEntryPath,
    isGuestKidsRoute,
    isGuestUser,
    showGuestPromotionPopup,
} from '../config/guestMode';
import { setGuestKidsMode } from '../guest/session';
import user from '../helpers/user';
import { useHeaderMenuQuery } from '../hooks/useHeaderMenuQuery';
import { useRedirection } from '../hooks/useRedirection';
import { ROUTE_CONSTANT } from '@libs/constants';

export type RouteCondition = {
    condition: boolean;
    redirectTo: string;
};

export const RouteGuard = ({
    conditions,
    context,
    children,
}: {
    conditions: RouteCondition[];
    context: any;
    children: React.ReactNode;
}) => {
    const params = useParams();
    const prefix = params['lang'] === 'en' ? 'en' : '';
    const location = useLocation();
    const navigate = useNavigate();
    const redirectTo = useRedirection();
    const profile = useSelector((store: IStore) => store.profile);
    const dispatch = useDispatch();
    const { data: consentData } = useConsentCheck(profile?.selectedProfile);
    const { data: headerMenuItems, isFetched: isHeaderMenuFetched, isLoading: isHeaderMenuLoading } =
        useHeaderMenuQuery();
    const guestOpenScreens = useMemo(() => getGuestOpenScreensFromMenu(headerMenuItems), [headerMenuItems]);

    useEffect(() => {
        const isProfileSelected =
            profile?.selectedProfile !== null && profile.selectedProfile?.profileGuid !== undefined;

        const isConsentPage = location.pathname === '/consent-management';
        const shouldNavigate = consentData?.consentNeeded && !isConsentPage;
        if (!context?.maxDeviceReached && shouldNavigate && isProfileSelected && !isGuestUser(user.getUserInfo())) {
            redirectTo(consentData.exitkid ? ROUTE_CONSTANT.EXIT_KID : ROUTE_CONSTANT.CONSENT_MANAGEMENT);
        }
    }, [consentData, context]);

    useEffect(() => {
        dispatch(fetchUserRevision());
    }, [location]);

    useEffect(() => {
        const currentUser = user.getUserInfo();
        if (!isGuestUser(currentUser)) {
            return;
        }

        if (!isHeaderMenuFetched || isHeaderMenuLoading) {
            return;
        }

        const primary = guestPrimaryRouteSegment(location.pathname);
        const inKidsMode = profile?.selectedProfile?.kidsMode === true;

        if (isGuestKidsEntryPath(location.pathname, location.search)) {
            setGuestKidsMode(true);
            navigate(buildLanguageRoute('/kids-home', prefix), { replace: true });
            return;
        }

        const isProfileSetupRoute =
            primary === 'select-profile' || primary === 'add-profile' || primary === 'cold-start';

        if (isProfileSetupRoute) {
            navigate(buildLanguageRoute('/', prefix), { replace: true });
            return;
        }

        const guestHome = inKidsMode ? '/kids-home' : '/';

        if (isGuestKidsRoute(primary)) {
            if (!isGuestAllowedPrimarySegment(primary, guestOpenScreens)) {
                showGuestPromotionPopup();
                navigate(buildLanguageRoute(guestHome, prefix), { replace: true });
                return;
            }
            if (!inKidsMode) {
                navigate(buildLanguageRoute('/', prefix), { replace: true });
            }
            return;
        }

        if ((primary === undefined || primary === '') && inKidsMode) {
            setGuestKidsMode(false);
            return;
        }

        if (isGuestAllowedPrimarySegment(primary, guestOpenScreens)) {
            return;
        }

        showGuestPromotionPopup();
        navigate(buildLanguageRoute(guestHome, prefix), { replace: true });
    }, [
        location.pathname,
        location.search,
        navigate,
        prefix,
        guestOpenScreens,
        isHeaderMenuFetched,
        isHeaderMenuLoading,
        profile?.selectedProfile?.kidsMode,
    ]);

    for (const guard of conditions) {
        if (!guard.condition) {
            return <Navigate to={buildLanguageRoute(guard.redirectTo, prefix)} replace />;
        }
    }
    return <>{children}</>;
};
